import { useCallback, useEffect, useState } from "react";
import { KeyRound, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { Card, Field, PageHeader, Td, Th } from "../../components/common/UI";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";

function UserDialog({ u, roles, onClose, onSaved }) {
  const [f, setF] = useState({ email: u.email || "", full_name: u.full_name || "", position: u.position || "", password: "", is_active: u.is_active ?? true, role_ids: (u.roles || []).map((r) => r.id) });
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const toggle = (id) => setF({ ...f, role_ids: f.role_ids.includes(id) ? f.role_ids.filter((x) => x !== id) : [...f.role_ids, id] });
  const save = async () => {
    try {
      u.id ? await api.put(`/users/${u.id}`, { full_name: f.full_name, position: f.position || null, is_active: f.is_active, role_ids: f.role_ids })
        : await api.post("/users", { email: f.email, full_name: f.full_name, position: f.position || null, password: f.password, role_ids: f.role_ids });
      toast.success("User saved");
      onClose();
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="user-dialog">
        <DialogHeader><DialogTitle>{u.id ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Email"><input disabled={!!u.id} className="otms-input" value={f.email} onChange={set("email")} data-testid="user-email-input" /></Field>
          <Field label="Full name"><input className="otms-input" value={f.full_name} onChange={set("full_name")} data-testid="user-name-input" /></Field>
          <Field label="Position"><input className="otms-input" value={f.position} onChange={set("position")} /></Field>
          {!u.id ? <Field label="Initial password" hint="Min. 8 chars with letters and numbers/symbols"><input type="password" className="otms-input" value={f.password} onChange={set("password")} data-testid="user-password-input" /></Field>
            : <label className="flex items-center gap-2 pt-6 text-xs"><input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} data-testid="user-active-checkbox" />Account active</label>}
          <div className="md:col-span-2">
            <div className="mb-1 text-xs font-medium text-slate-600">Roles</div>
            <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
              {roles.map((r) => <label key={r.id} className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.role_ids.includes(r.id)} onChange={() => toggle(r.id)} data-testid={`user-role-${r.code}`} />{r.name}</label>)}
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid="user-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleMatrix({ roles, perms, onSaved }) {
  const [sel, setSel] = useState(roles[0]?.id);
  const role = roles.find((r) => r.id === sel);
  const [codes, setCodes] = useState(role?.permissions || []);
  useEffect(() => { setCodes(role?.permissions || []); }, [role]);
  const save = () => api.put(`/roles/${sel}/permissions`, { permission_codes: codes }).then(() => { toast.success("Permissions updated"); onSaved(); }).catch((e) => toast.error(errMsg(e)));
  const create = async () => {
    const name = window.prompt("New role name");
    if (!name) return;
    const code = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    try { await api.post("/roles", { code, name, permission_codes: ["measure.view"] }); toast.success("Role created"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const cats = [...new Set(perms.map((p) => p.category))];
  return (
    <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
      <Card title="Roles" actions={<Button size="sm" variant="outline" onClick={create} data-testid="add-role-button"><Plus className="h-3.5 w-3.5" /></Button>}>
        <ul className="space-y-0.5">
          {roles.map((r) => (
            <li key={r.id}><button onClick={() => setSel(r.id)} data-testid={`role-item-${r.code}`} className={`flex w-full justify-between rounded px-2 py-1.5 text-left text-xs ${sel === r.id ? "bg-sky-50 font-semibold text-sky-800" : "hover:bg-slate-50"}`}><span>{r.name}</span><span className="text-slate-400">{r.user_count}</span></button></li>
          ))}
        </ul>
      </Card>
      {role && (
        <Card title={`Permissions — ${role.name}`} actions={<Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid="save-role-permissions">Save</Button>} testId="role-permissions">
          <div className="grid gap-5 md:grid-cols-2">
            {cats.map((c) => (
              <div key={c}>
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{c}</div>
                {perms.filter((p) => p.category === c).map((p) => (
                  <label key={p.code} className="flex items-start gap-2 py-0.5 text-xs">
                    <input type="checkbox" className="mt-0.5" checked={codes.includes(p.code)} onChange={() => setCodes(codes.includes(p.code) ? codes.filter((x) => x !== p.code) : [...codes, p.code])} data-testid={`perm-${p.code}`} />
                    <span><code className="text-[11px]">{p.code}</code> <span className="text-slate-500">{p.description}</span></span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

export default function UsersRoles() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/roles").then((r) => setRoles(r.data));
    api.get("/permissions").then((r) => setPerms(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const setPw = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.email}`);
    if (!pw) return;
    try { await api.post(`/users/${u.id}/set-password`, { new_password: pw }); toast.success("Password set; existing sessions revoked"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Administration" title="Users and Roles" subtitle="Accounts and permissions apply only within this LGU." />
      <Tabs defaultValue="users">
        <TabsList className="mb-4"><TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger><TabsTrigger value="roles" data-testid="tab-roles">Roles &amp; permissions</TabsTrigger></TabsList>
        <TabsContent value="users">
          <div className="mb-3 flex justify-end"><Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => setEdit({})} data-testid="add-user-button"><Plus className="mr-1 h-4 w-4" />New user</Button></div>
          <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-[820px]" data-testid="users-table">
              <thead><tr><Th>Name</Th><Th>Email</Th><Th>Roles</Th><Th>Status</Th><Th>Last login</Th><Th /></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <Td className="font-medium">{u.full_name}<div className="text-[11px] font-normal text-slate-500">{u.position}</div></Td>
                    <Td>{u.email}</Td>
                    <Td>{u.roles.map((r) => r.name).join(", ")}</Td>
                    <Td>{u.is_active ? <span className="text-emerald-700">Active</span> : <span className="text-red-600">Inactive</span>}</Td>
                    <Td>{fmtDateTime(u.last_login_at)}</Td>
                    <Td className="whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setPw(u)} data-testid={`set-password-${u.id}`}><KeyRound className="h-3.5 w-3.5" /></Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="roles">{roles.length > 0 && <RoleMatrix roles={roles} perms={perms} onSaved={load} />}</TabsContent>
      </Tabs>
      {edit && <UserDialog u={edit} roles={roles} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
}
